import { useCallback, useRef, useState } from "react";
import { modelsProxyUrl } from "../../../../../API/axiosInstance";

const TOPICS_STREAM_URL = modelsProxyUrl("/topics");

/**
 * Persistent SSE connection for real-time topic card updates.
 *
 * Opened ONCE on mount and kept alive for the page lifecycle.
 * When an event arrives it merges status / pipeline_status /
 * submission_status / post_counts into the matching topic in allTopics.
 *
 * post_counts can be:
 *   - a number  → mapped to data_count
 *   - { total, processed, unprocessed }
 *   - { data_count, processed_posts, unprocessed_posts }
 */
export default function useTopicsSSE({ setAllTopics }) {
  const esRef = useRef(null);
  const [connStatus, setConnStatus] = useState("idle");

  const applyUpdate = useCallback(
    (raw) => {
      try {
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;
        const id = data.topic_id || data._id;
        if (!id) return;

        setAllTopics((prev) =>
          prev.map((topic) => {
            if (topic._id !== id) return topic;

            const next = { ...topic };

            if (data.status !== undefined) next.status = data.status;
            if (data.pipeline_status !== undefined)
              next.pipeline_status = data.pipeline_status;
            if (data.submission_status !== undefined)
              next.submission_status = data.submission_status;

            if (data.post_counts !== undefined) {
              const pc = data.post_counts;
              if (typeof pc === "number") {
                next.data_count = pc;
              } else if (pc && typeof pc === "object") {
                if (pc.total !== undefined) next.data_count = pc.total;
                if (pc.data_count !== undefined) next.data_count = pc.data_count;
                if (pc.processed !== undefined) next.processed_posts = pc.processed;
                if (pc.processed_posts !== undefined) next.processed_posts = pc.processed_posts;
                if (pc.unprocessed !== undefined) next.unprocessed_posts = pc.unprocessed;
                if (pc.unprocessed_posts !== undefined) next.unprocessed_posts = pc.unprocessed_posts;
              }
            }

            return next;
          }),
        );
      } catch (err) {
        console.error("[topics-sse] parse error:", err);
      }
    },
    [setAllTopics],
  );

  const openSSE = useCallback(() => {
    if (esRef.current) return;

    setConnStatus("connecting");

    const es = new EventSource(TOPICS_STREAM_URL, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => setConnStatus("live");

    es.addEventListener("topic_update", (e) => applyUpdate(e.data));
    es.onmessage = (e) => applyUpdate(e.data);

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnStatus("error");
        esRef.current = null;
      } else {
        setConnStatus("connecting");
      }
    };
  }, [applyUpdate]);

  const closeSSE = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setConnStatus("idle");
  }, []);

  const reconnectSSE = useCallback(() => {
    closeSSE();
    setTimeout(openSSE, 100);
  }, [closeSSE, openSSE]);

  return { connStatus, openSSE, closeSSE, reconnectSSE };
}
